import { useRef } from 'react';
import Script from 'next/script';
import {
  composeValidators,
  isPhone,
  isRequired,
  phoneNumberMask,
  phoneNumberUnmask,
  minLength,
} from '@belong/common';
import { Form, PasswordField, TextField } from '@belong/ui';
import clsx from 'clsx';
import { omit } from 'es-toolkit/compat';
import { RegisterUserModel } from 'src/api/models';
import { EmailField } from 'src/components/email-field/email-field';
import {
  SmsConsentField,
  SMS_CONSENT_FIELD_NAME,
} from 'src/features/auth/components/sms-consent-field/sms-consent-field';
import { useLogin, useRegister } from 'src/features/auth/queries';
import { AUTH_STRINGS } from 'src/features/auth/strings';
import type { SignUpFormSchema } from 'src/features/auth/types';
import { useReddit } from 'src/features/document/hooks';
import css from './sign-up-form.module.css';

const recaptchaSiteKey = process.env.NEXT_PUBLIC_GOOGLE_RECAPTCHA_SITEKEY || '';

const INITIAL_VALUES: SignUpFormSchema = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  smsConsent: false,
};

type Props = {
  enforcedEmail?: string;
  onError?: (error: unknown, values?: RegisterUserModel) => void;
  onFinish:
    | ((hasError: boolean) => Promise<void>)
    | ((hasError: boolean) => void);
  onStart?: () => void;
  onSuccess?: (() => Promise<void>) | (() => void);
  shouldAskForPassword?: boolean;
  shouldAskForSmsConsent?: boolean;
};

export function SignUpForm({
  enforcedEmail,
  onError = () => {},
  onFinish = async () => {},
  onStart = () => {},
  onSuccess = async () => {},
  shouldAskForPassword = false,
  shouldAskForSmsConsent = false,
}: Props) {
  const hasError = useRef(false);
  const { register } = useRegister();
  const { login } = useLogin();
  const reddit = useReddit();

  async function handleSubmit(values: SignUpFormSchema) {
    // The consent checkbox gates the CTA only: `RegisterUserModel` has no such
    // field, and `registerService` posts the model as-is.
    const registrationValues = omit(values, [SMS_CONSENT_FIELD_NAME]);

    onStart();

    reddit.track('Lead');

    window.grecaptcha.ready(async () => {
      try {
        await register(registrationValues as RegisterUserModel);
        if (shouldAskForPassword) {
          const recaptchaToken = await window.grecaptcha.execute(
            recaptchaSiteKey,
            { action: 'submit' },
          );
          await login({
            email: values.email,
            password: values.password as string,
            recaptchaToken,
          });
        }
        await onSuccess();
        hasError.current = false;
      } catch (error) {
        hasError.current = true;
        onError(error, registrationValues as RegisterUserModel);
      } finally {
        await onFinish(hasError.current);
      }
    });
  }

  const initialValues = !enforcedEmail
    ? INITIAL_VALUES
    : { ...INITIAL_VALUES, email: enforcedEmail };

  return (
    <Form initialValues={initialValues} onSubmit={handleSubmit}>
      {({ isSubmitting, isValid, values }) => {
        return (
          <div className={clsx('w-full', css.container)}>
            <div className={clsx('mb-xl w-full')}>
              <div
                className={clsx(
                  'md:gap-x-lg contents md:grid md:w-full md:grid-cols-2',
                  shouldAskForPassword ? 'mb-sm' : 'mb-lg',
                )}
              >
                <div
                  className={clsx(
                    'md:w-full',
                    shouldAskForPassword ? 'mb-sm' : 'mb-lg',
                  )}
                >
                  <TextField
                    label={AUTH_STRINGS['first.name']}
                    name="firstName"
                    validate={isRequired}
                  />
                </div>
                <div
                  className={clsx(
                    'md:w-full',
                    shouldAskForPassword ? 'mb-sm' : 'mb-lg',
                  )}
                >
                  <TextField
                    label={AUTH_STRINGS['last.name']}
                    name="lastName"
                    validate={isRequired}
                  />
                </div>
              </div>
              <div className={shouldAskForPassword ? 'mb-sm' : 'mb-lg'}>
                <EmailField
                  id="sign-up-email"
                  label={AUTH_STRINGS.email}
                  name="email"
                  readOnly={!!enforcedEmail}
                />
              </div>
              <div className={shouldAskForPassword ? 'mb-sm' : 'mb-lg'}>
                <TextField
                  label={AUTH_STRINGS.phone}
                  mask={phoneNumberMask}
                  name="phone"
                  unmask={phoneNumberUnmask}
                  validate={composeValidators(isRequired, isPhone)}
                />
              </div>

              {shouldAskForPassword && (
                <div className="mb-sm">
                  <PasswordField
                    label={AUTH_STRINGS.password}
                    name="password"
                    validate={composeValidators(isRequired, minLength(6))}
                  />
                </div>
              )}

              {shouldAskForSmsConsent && (
                <div className="mt-sm">
                  <SmsConsentField />
                </div>
              )}
            </div>
            <div className="w-full">
              <Form.SubmitButton
                disabled={
                  !isValid ||
                  isSubmitting ||
                  (shouldAskForSmsConsent && values.smsConsent !== true)
                }
                size="fluid"
              >
                {AUTH_STRINGS['get.started']}
              </Form.SubmitButton>
            </div>
            <Script
              src={`https://www.google.com/recaptcha/api.js?render=${recaptchaSiteKey}`}
            />
          </div>
        );
      }}
    </Form>
  );
}
